/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAtom } from 'jotai';
import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { HiX } from 'react-icons/hi';
import { useBoolean, useEffectOnce, useWindowSize } from 'usehooks-ts';

import { getSSRInitialListings } from '@/api/elasticsearch/server';
import { GetUserById } from '@/api/fetch/marketplace-api';
import { UserByIDRequest } from '@/api/gen-protos/users_pb';
import { rudderstackTrackEvent } from '@/api/rudderstack/client';
import { APP_CONFIG } from '@/app-config';
import { computeAdsNearestPriceRange } from '@/lib/helpers';
import { useGoogleAdsTargeting } from '@/lib/hooks/useGoogleAdsTrageting';
import { LoadHeavyContentAtom } from '@/lib/store';
import { cleanObject } from '@/lib/utils';
import { User } from '@/models/user';
import FeaturedClassifiedsCarousel from '@/modules/core/components/FeaturedClassifiedsCarousel/FeaturedClassifiedsCarousel';
import Layout from '@/modules/core/components/Layout';
import NarrowFilter, {
  ICriteria,
} from '@/modules/core/components/NarrowFilter';
import RecommendedShops from '@/modules/core/components/RecommendedShops';
import {
  SearchedListingsAction,
  searchResultsTextRefAtom,
} from '@/modules/core/store/store';
import { IListingItem } from '@/modules/core/types';
import { Meta } from '@/seo/Meta';
import AdList_ from '@/tayara-kit/AdList_';
import TargtedBanners from '@/tayara-kit/banners/TargtedBanners';
import Text from '@/tayara-kit/Text';
import { NEXT_API_URL } from 'environment';

import { NextPageWithLayout } from './_app';

const SearchQueryBanner = dynamic(
  async () => import('@/tayara-kit/banners/SearchQueryBanner'),
  {
    ssr: false,
  }
);

const TopMobile = dynamic(
  async () => import('@/tayara-kit/banners/TopMobile'),
  {
    ssr: false,
  }
);

// TODO uncommend this when the logic is propery implemented
// const OPTIONS: SelectOption[] = [
//   { label: 'Le plus cher', value: 'price:desc' },
//   { label: 'Le moins cher', value: 'price:asc' },
//   { label: 'Le plus récent', value: 'creationtimestamp:asc' },
//   { label: 'Le plus pertinent', value: 'most-relevant' },
//   { label: 'Le plus visité', value: 'most-viewed' },
// ];

export interface Props {
  searchedListingsAction: SearchedListingsAction | undefined;
  goldenListing: IListingItem | null;

  asideRefWidth: number;
}

interface IGenerateSearchPageTitle {
  q?: string;
  category?: string;
  subcategory?: string;
  governorate?: string;
  delegation?: string;
  productType?: 'Occasion' | 'Neuf' | '';
}
const generateSearchPageTitle = ({
  q,
  category,
  subcategory,
  governorate,
  delegation,
  productType,
}: IGenerateSearchPageTitle): string => {
  let title = '';

  const c = subcategory || category;
  const l = delegation || governorate;
  const t = (() => {
    switch (productType) {
      case 'Neuf':
        return 'neuf';
      case 'Occasion':
        return 'occasion';
      default:
        return '';
    }
  })();

  if (q && t) title += `${q} ${t}`;
  else if (q) title += `${q}`;

  if (c && !q) title += `${c}`;
  else if (c) title += ` dans ${c}`;
  if (c && t && !q) title += ` ${t}`;
  if (l && !q && !t && !c) title += `Annonces à ${l}`;
  else if (l) title += ` à ${l}`;

  if (!title)
    title = `Rechercher de l'immobilier, des voitures, des smartphones et PCs, et plus sur tayara`;

  return title;
};

const Search: NextPageWithLayout<Props> = (props: Props) => {
  // const { query } = useRouter();

  const [loadHeavyContent] = useAtom(LoadHeavyContentAtom);
  const [recommendedShopsIds, setRecommendedShopsIds] = useState('');
  const [agenceShopsIds, setAgenceShopsIds] = useState('');
  const [showRoomShopsIds, setShowRoomShopsIds] = useState('');
  const [multimediaShopsIds, setMultimediaShopsIds] = useState('');
  // Added immoneuf state
  const [immoneufShopsIds, setImmoneufShopsIds] = useState<string[]>([]);
  const [agenciesShopData, setAgenciesShopData] = useState<User[]>([]);
  const [showRoomsShopsData, setShowRoomsShopsData] = useState<User[]>([]);
  const [multimediaShopsData, setMultimediaShopsData] = useState<User[]>([]);
  // Added immoneuf shop data state
  const [immoneufShopsData, setImmoneufShopsData] = useState<User[]>([]);
  const [totalHitsCount, setTotalHitsCount] = useState(0);
  const { value: googleAdClosed, setTrue: closeGoogleAd } = useBoolean(false);
  const searchResultsTextRef = useRef<HTMLDivElement | null>(null);
  const [, setSearchResultsTextRefStore] = useAtom(searchResultsTextRefAtom);

  useEffect(() => {
    setSearchResultsTextRefStore(searchResultsTextRef);
  }, [searchResultsTextRef, setSearchResultsTextRefStore]);

  const windowSize = useWindowSize();

  const RecommendedShopsTitles = {
    agenciesShopsTitle: 'Agences recommandées',
    showRoomShopsTitle: 'Showrooms recommandés',
    multimediaShopsTitle: 'Shops recommandés',
    // Added immoneuf title
    immoneufShopsTitle: 'Nos projets recommandés',
  };

  const getUserData = async (kcid: string) => {
    const req = new UserByIDRequest();
    req.setKcid(kcid);
    const userData = await GetUserById(req);
    const userFormat: User = {
      avatar: userData.getUser()?.getCurrentstate()?.getAvatar(),
      kcid: userData?.getUser()?.getKcid(),
      productType: userData?.getUser()?.getProducttype(),
      userType: userData?.getUser()?.getUsertype(),
      phonenumber: userData.getUser()?.getCurrentstate()?.getPhonenumber(),
      shopBackground: userData.getUser()?.getShopbackgroundimage(),
      fullname: userData.getUser()?.getCurrentstate()?.getFullname(),
      email: userData.getUser()?.getCurrentstate()?.getEmail(),
    };

    return userFormat;
  };

  useEffectOnce(() => {
    fetch(`${NEXT_API_URL}/settings`)
      .then((res) => res.json())
      .then((settings) => {
        if (settings?.recommendedShops !== undefined) {
          const agences = settings?.recommendedShops.filter(
            (shop: any) => shop.recommendedShopCategory === 'agence'
          );
          const showRooms = settings?.recommendedShops.filter(
            (shop: any) => shop.recommendedShopCategory === 'showRoom'
          );
          const multimediaShops = settings?.recommendedShops.filter(
            (shop: any) => shop.recommendedShopCategory === 'multimedia'
          );
          // Added immoneuf shops filtering
          const immoneufShops = settings?.recommendedShops.filter(
            (shop: any) => shop.recommendedShopCategory === 'immoneuf'
          );

          setAgenceShopsIds(agences.map((shop: any) => shop.recommendedShopId));
          setShowRoomShopsIds(
            showRooms.map((shop: any) => shop.recommendedShopId)
          );
          setMultimediaShopsIds(
            multimediaShops.map((shop: any) => shop.recommendedShopId)
          );
          // Added immoneuf shops IDs setting
          setImmoneufShopsIds(
            immoneufShops.map((shop: any) => shop.recommendedShopId)
          );
          setRecommendedShopsIds(
            settings?.recommendedShops.map(
              (recommendedShop: any) => recommendedShop.recommendedShopId
            )
          );
        }
      })
      .catch((error) => {
        console.error('Error fetching settings:', error);
      });
  });

  useEffect(() => {
    if (Array.isArray(agenceShopsIds) && agenceShopsIds.length > 0) {
      const fetchShopData = async () => {
        try {
          const promises = agenceShopsIds.map((id: string) => getUserData(id));
          const userData = await Promise.all(promises);
          setAgenciesShopData(userData);
        } catch (error) {
          console.error('Error fetching agency shop data:', error);
        }
      };
      fetchShopData();
    }
    if (Array.isArray(showRoomShopsIds) && showRoomShopsIds.length > 0) {
      const fetchShopData = async () => {
        try {
          const promises = showRoomShopsIds.map((id: string) =>
            getUserData(id)
          );
          const userData = await Promise.all(promises);
          setShowRoomsShopsData(userData);
        } catch (error) {
          console.error('Error fetching showroom shop data:', error);
        }
      };
      fetchShopData();
    }
    if (Array.isArray(multimediaShopsIds) && multimediaShopsIds.length > 0) {
      const fetchShopData = async () => {
        try {
          const promises = multimediaShopsIds.map((id: string) =>
            getUserData(id)
          );
          const userData = await Promise.all(promises);
          setMultimediaShopsData(userData);
        } catch (error) {
          console.error('Error fetching multimedia shop data:', error);
        }
      };
      fetchShopData();
    }
    // Added immoneuf shops data fetching
    if (Array.isArray(immoneufShopsIds) && immoneufShopsIds.length > 0) {
      const fetchShopData = async () => {
        try {
          const promises = immoneufShopsIds.map((id: string) =>
            getUserData(id)
          );
          const userData = await Promise.all(promises);
          setImmoneufShopsData(userData);
        } catch (error) {
          console.error('Error fetching immoneuf shop data:', error);
        }
      };
      fetchShopData();
    }
  }, [
    recommendedShopsIds,
    agenceShopsIds,
    showRoomShopsIds,
    multimediaShopsIds,
    // Added immoneufShopsIds to dependency array
    immoneufShopsIds,
  ]);

  const { query } = useRouter();
  const { category, subcategory, governorate, delegation, q, productType } = {
    q: query.q && decodeURIComponent(query.q as string),
    category: query.category && decodeURIComponent(query.category as string),
    subcategory:
      query.subCategory && decodeURIComponent(query.subCategory as string),
    governorate:
      query.governorate && decodeURIComponent(query.governorate as string),
    delegation:
      query.delegation && decodeURIComponent(query.delegation as string),
    productType: (query.productType &&
      decodeURIComponent(query.productType as 'Occasion' | 'Neuf' | '')) as
      | 'Occasion'
      | 'Neuf'
      | '',
  };

  useEffect(() => {
    rudderstackTrackEvent('page_view', {
      page_type: decodeURIComponent(window?.location?.pathname),
    });
    rudderstackTrackEvent('Visited Listing Screen', {
      Category: category,
      SubCategory: subcategory,
      Governorate: governorate,
      Delegation: delegation,
    });
  }, [category, subcategory, delegation, governorate]);
  const title = generateSearchPageTitle({
    q,
    category,
    delegation,
    governorate,
    productType,
    subcategory,
  });

  const targeting = useMemo(() => {
    const t = Object.create({});
    if (
      typeof query.minPrice === 'string' &&
      typeof query.maxPrice === 'string'
    )
      t.priceRange = computeAdsNearestPriceRange(
        parseInt(query.minPrice, 10),
        parseInt(query.maxPrice, 10)
      );
    Object.keys(query).forEach((k) => {
      t[k.replaceAll(' ', '')] = query[k];
    });
    return t;
  }, [query]);
  useGoogleAdsTargeting(targeting);

  // const shopId = '642fd5cc39b6f183fc7fcbbc';
  // const viewedClassifiedId = '642fd5cc39b6f183fc7fcbbc';
  // const [shopUserData, setShopUserData] = useState<IUserData>();

  // useEffect(() => {
  //   getUserData(shopId)
  //     .then((data) => {
  //       setShopUserData(data);
  //     })
  //     .catch((error) => {
  //       console.error(error);
  //     });
  // }, [shopId]);
  return (
    <>
      <Meta
        title={`${title} | ${APP_CONFIG.site_name}`}
        description={APP_CONFIG.description}
      />

      {!googleAdClosed && windowSize.width > 1024 && (
        <div className="realtive w-full h-[130px] md:h-[300px] flex justify-center items-center mx-auto bg-no-repeat bg-contain bg-local bg-center md:mt-5">
          <>
            <div className="absolute  w-[50%] md:aspect-1000/300 md:max-w-[1000px] mx-auto mb-1 border border-neutral-200 bg-neutral-50/50 rounded-2xl overflow-hidden">
              <button
                tabIndex={-1}
                className="p-1 rounded-full md:bg-neutral-100 text-neutral-400 absolute top-0 right-0 md:mr-4 md:mt-4 md:border border-neutral-300 "
                onClick={closeGoogleAd}
              >
                <HiX size={19} />
              </button>
              <div className="flex flex-col justify-center items-center w-full h-full">
                <span className=" text-2xl md:text-4xl font-extrabold text-neutral-200">
                  Publicité
                </span>{' '}
                <img
                  className={`w-[50px] md:w-[80px] relative -right-6 md:-right-8 transition-all duration-300 `}
                  src="/media/tayara-logo.svg"
                  alt="Logo de tayara.tn"
                />
              </div>
            </div>
          </>

          {loadHeavyContent && q !== undefined ? (
            <SearchQueryBanner query={q as string} />
          ) : (
            <TargtedBanners category={category as string} />
          )}
        </div>
      )}

      {windowSize.width <= 1024 && !googleAdClosed ? (
        <div
          className="relative w-full h-[130px] md:h-[300px] flex justify-center items-center mx-auto bg-no-repeat bg-contain bg-local bg-center md:mt-5"
          style={{
            backgroundImage: `url(/media/pubcover.png)`,
          }}
        >
          <>
            <div className="relative h-[100px] md:h-[300px] w-[320px] md:w-full md:aspect-1000/300 md:max-w-[1000px] mx-auto mb-1 border border-neutral-200 bg-neutral-50/50 rounded-2xl overflow-hidden">
              <button
                tabIndex={-1}
                className="p-1 rounded-full md:bg-neutral-100 text-neutral-400 absolute top-0 right-0 md:mr-4 md:mt-4 md:border border-neutral-300 "
                onClick={closeGoogleAd}
              >
                <HiX size={19} />
              </button>
              <div className="flex flex-col justify-center items-center w-full h-full">
                <span className=" text-2xl md:text-4xl font-extrabold text-neutral-200">
                  Publicité
                </span>{' '}
                <img
                  className={`w-[50px] md:w-[80px] relative -right-6 md:-right-8 transition-all duration-300 `}
                  src="/media/tayara-logo.svg"
                  alt="Logo de tayara.tn"
                />
              </div>
              {loadHeavyContent ? <TopMobile /> : <></>}
            </div>
          </>
        </div>
      ) : (
        // </div>
        <></>
      )}

      <div className="mt-3 mx-2 lg:ml-0 lg:mt-12 relative z-10">
        <div className="relative -z-50">
          <FeaturedClassifiedsCarousel
            ssrSearchedListingsAction={props.searchedListingsAction}
            ssrGoldenListing={props.goldenListing}
            category={category}
          />
          {category === 'Immobilier' && agenciesShopData.length > 0 && (
            <RecommendedShops
              showRoomsShopsData={agenciesShopData}
              title={RecommendedShopsTitles.agenciesShopsTitle}
            />
          )}
          {category === 'Véhicules' && showRoomsShopsData.length > 0 && (
            <RecommendedShops
              showRoomsShopsData={showRoomsShopsData}
              title={RecommendedShopsTitles.showRoomShopsTitle}
            />
          )}
          {category === 'Informatique et Multimedias' &&
            multimediaShopsData.length > 0 && (
              <RecommendedShops
                showRoomsShopsData={multimediaShopsData}
                title={RecommendedShopsTitles.multimediaShopsTitle}
              />
            )}
          {/* Added ImmoNeuf recommended shops section */}
          {category === 'ImmoNeuf' && immoneufShopsData.length > 0 && (
            <RecommendedShops
              showRoomsShopsData={immoneufShopsData}
              title={RecommendedShopsTitles.immoneufShopsTitle}
            />
          )}
        </div>

        {/* <MoreFromIqos
          shopId={shopId}
          viewedClassifiedId={viewedClassifiedId}
          shopUserData={shopUserData}
        /> */}

        {/* Results header and sorting */}
        <div className="flex flex-col gap-3 lg:flex-row my-2">
          {/* West-side (on the left) */}
          <div className="flex flex-wrap lg:flex-row flex-col items-center gap-2">
            <div ref={searchResultsTextRef}>
              <Text
                text={title}
                as="h1"
                className="text-2xl font-bold text-gray-700"
              />
            </div>
            <Text
              text={`(${totalHitsCount} annonces trouvées)`}
              as="data"
              className="block mt-1 text-sm lg:text-base font-bold text-info"
            />
          </div>
          {/* West-side (on the right) */}
          <div className="flex flex-col gap-2 lg:flex-row">
            {/* *** *** *** */}
            {/* Temporarily commented, until the code is properly implemented */}
            {/* <Select
              displayLabel="Trier par"
              className="rounded-full select-sm w-[160px] max-w-full"
              options={OPTIONS}
              onChange={(event) => {
                setSort(event.target.value);
              }}
            /> */}
          </div>
        </div>

        {/* Publisher list  */}

        <AdList_
          onChangeTotalHitsCount={(newTotalHitsCount) => {
            setTotalHitsCount(newTotalHitsCount);
          }}
          category={category as string}
          ssrSearchedListingsAction={props.searchedListingsAction}
        />
      </div>
    </>
  );
};
export default Search;

// Dynamic layout configuration
Search.getLayout = function getLayout(page: JSX.Element, props: Props) {
  return (
    <Layout>
      <Layout.WithSidePanel
        aside={
          <NarrowFilter
            asideRefWidth={props.asideRefWidth}
            _nextI18Next={{
              userConfig: null,
              initialLocale: 'en',
              initialI18nStore: {},
            }}
          />
        }
      >
        {page}
      </Layout.WithSidePanel>
    </Layout>
  );
};

// Server side code and i18n
export const getServerSideProps: GetServerSideProps = async ({
  locale,
  query,
}): Promise<GetServerSidePropsResult<ICriteria>> => {
  const [searchedListingsAction, goldenListing] = await getSSRInitialListings(
    query
  );

  return {
    props: cleanObject({
      ...(await serverSideTranslations(locale || 'en')),
      asideRefWidth: 0,
      searchedListingsAction,
      goldenListing,
    }),
  };
};
